import { debounce } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { Fragrance } from '~/api/api.declaration';
import { FragranceService } from '~/service/service.fragrance';
import { NoteService } from '~/service/service.note';
import { FragranceCreateDto } from '~/shared/dto/admin/fragrance.dto';
import { Concentration, Longevity, Occasion, Season, Sillage, Weather } from '~/shared/dto/model.enums';
import { Form } from '~/shared/form';
import { useService } from '~/shared/service/service.base';
import { FieldAutocomplete } from '~/view/fields/field.autocomplete';
import { FieldEnum, FieldMultiEnum } from '~/view/fields/field.enum';
import { Col, Row } from '~/view/view.box';

type FragranceNoseProps = {
	form: Form<FragranceCreateDto, Fragrance>;
};

export const FragranceNose = observer<FragranceNoseProps>(({ form }) => {
	const [fragrances, notes] = useService(FragranceService, NoteService);
	const [reminds, setReminds] = useState<Pick<Fragrance, 'id' | 'name'>[]>([]);

	useEffect(() => {
		if (form.initial?.noseRemindsFragrances?.length) {
			fragrances.getByIds(form.initial.noseRemindsFragrances).then(fetched => setReminds(fetched));
		}
	}, [form.initial?.noseRemindsFragrances, fragrances]);

	const fetchRemindsOptions = useMemo(
		() =>
			debounce(async (inputValue: string) => {
				try {
					if (isNaN(+inputValue) || !inputValue) return;
					const fragrance = await fragrances.getOne(+inputValue);
					if (!fragrance) return;

					setReminds(prev => {
						if (prev.find(el => el.id === fragrance.id)) return [...prev];
						return [fragrance, ...prev];
					});
				} catch (e) {
					console.error(e);
				}
			}, 400),
		[fragrances, setReminds],
	);

	return (
		<>
			<Col gap={2}>
				<Row gap={4}>
					<FieldEnum field={form.$noseSillage} enumeration={Sillage} enumName="Sillage" nullLabel="" />
					<FieldEnum field={form.$noseLongevity} enumeration={Longevity} enumName="Longevity" nullLabel="" />
					<FieldEnum
						field={form.$noseConcentration}
						enumeration={Concentration}
						enumName="Concentration"
						nullLabel=""
					/>
				</Row>
				<Row gap={4}>
					<FieldMultiEnum field={form.$noseWeather} enumeration={Weather} enumName="Weather" />
					<FieldMultiEnum field={form.$noseOccasion} enumeration={Occasion} enumName="Occasion" />
					<FieldMultiEnum field={form.$noseSeason} enumeration={Season} enumName="Season" />
				</Row>
			</Col>
			<FieldAutocomplete
				items={notes.names}
				valueProp="id"
				labelProp="name"
				enabledProp="active"
				field={form.$noseNotes}
			/>
			<FieldAutocomplete
				items={reminds}
				valueProp="id"
				labelProp="name"
				placeholder="Search by Fragrance ID"
				onChange={event => fetchRemindsOptions(event.target.value)}
				field={form.$noseRemindsFragrances}
			/>
		</>
	);
});
